import { ColumnFiltersState } from '@tanstack/react-table';
import { z } from 'zod';

import {
  getInconsistencySourceDisplayName,
  getInconsistencyTypeDisplayName,
  InconsistencySource,
  InconsistencyType,
} from '../../../backend/src/matter/inconsistency/inconsistency-display';
import { capitalizeFirstLetter } from '../helpers/filterNames';
import { InconsistencyFilters } from '@/api/queries/useGetAllSubsetInconsistencies';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';
import { DocFilters, FactFilters } from '@/types';
import {
  getExtensionLabel,
  getDocumentTypeLabel,
  getRelevanceLabel,
  getClassificationLabel,
  getPersonLabel,
  getProcessedLabel,
  getDateLabel,
} from '@/utils/filterLookups';

// Zod schema for date range filter values
const DateRangeFilterSchema = z.object({
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
});

export type DateRangeFilterValue = z.infer<typeof DateRangeFilterSchema>;

export interface FilterPill {
  id: string;
  label: string;
  color: string;
  onRemove?: () => void;
  nonVisibleItems?: string[];
  icon?: string; // Lucide icon class name (e.g., 'lucide--database')
}

// Shared filter pill builders
function addDateRangePills(
  pills: FilterPill[],
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  onRemoveFilter: (key: string) => void,
) {
  if (startDate) {
    pills.push({
      id: 'startDate',
      label: getDateLabel(startDate, 'From'),
      color: CHIP_COLOURS.filters.date,
      onRemove: () => onRemoveFilter('startDate'),
    });
  }

  if (endDate) {
    pills.push({
      id: 'endDate',
      label: getDateLabel(endDate, 'To'),
      color: CHIP_COLOURS.filters.date,
      onRemove: () => onRemoveFilter('endDate'),
    });
  }
}

function addDocumentTypesPills(
  pills: FilterPill[],
  documentTypes: string[] | undefined,
  onRemoveFilter: (key: string, value: string) => void,
) {
  if (documentTypes?.length) {
    const maxVisible = 3;
    const visibleTypes = documentTypes.slice(0, maxVisible);
    const remainingCount = documentTypes.length - maxVisible;

    visibleTypes.forEach((type, index) => {
      pills.push({
        id: `docType-${type}-${index}`,
        label: getDocumentTypeLabel(type),
        color: CHIP_COLOURS.type,
        onRemove: () => onRemoveFilter('documentTypes', type),
      });
    });

    if (remainingCount > 0) {
      pills.push({
        id: 'docType-more',
        label: `+${remainingCount} more`,
        color: CHIP_COLOURS.type,
        nonVisibleItems: documentTypes.slice(maxVisible),
      });
    }
  }
}

function addThemesPills(
  pills: FilterPill[],
  themes: string[] | undefined,
  filterKey: string,
  onRemoveFilter: (key: string, value: string) => void,
) {
  if (themes?.length) {
    themes.forEach((theme, index) => {
      pills.push({
        id: `theme-${theme}-${index}`,
        label: theme,
        color: CHIP_COLOURS.issue,
        onRemove: () => onRemoveFilter(filterKey, theme),
      });
    });
  }
}

function addIssuesPills(
  pills: FilterPill[],
  issueIds: string[] | undefined,
  onRemoveFilter: (key: string, value: string) => void,
  issuesMap?: Map<string, string>,
) {
  if (issueIds?.length) {
    issueIds.forEach((issueId, index) => {
      pills.push({
        id: `issue-${issueId}-${index}`,
        label: issuesMap?.get(issueId) ?? issueId,
        color: CHIP_COLOURS.issue,
        onRemove: () => onRemoveFilter('issueIds', issueId),
      });
    });
  }
}

export function buildDocumentFilterPills(
  filters: DocFilters,
  onRemoveFilter: (key: string, value?: any) => void,
  relativityFieldDisplayNames?: Record<string, string>,
  runsMap?: Map<string, string>,
): FilterPill[] {
  const pills: FilterPill[] = [];

  // Processed status
  if (filters.processed !== null && filters.processed !== undefined) {
    pills.push({
      id: 'processed',
      label: getProcessedLabel(filters.processed),
      color: CHIP_COLOURS.default,
      onRemove: () => onRemoveFilter('processed'),
    });
  }

  // Extensions (file types)
  if (filters.extensions?.length) {
    filters.extensions.forEach((ext, index) => {
      pills.push({
        id: `extension-${ext}-${index}`,
        label: getExtensionLabel(ext),
        color: CHIP_COLOURS.extension,
        onRemove: () => onRemoveFilter('extensions', ext),
      });
    });
  }

  addDocumentTypesPills(pills, filters.documentTypes, onRemoveFilter);
  addThemesPills(pills, filters.documentIssues, 'documentIssues', onRemoveFilter);
  addDateRangePills(pills, filters.startDate, filters.endDate, onRemoveFilter);

  // Processing runs
  if (filters.runIds?.length) {
    filters.runIds.forEach((runId, index) => {
      pills.push({
        id: `run-${runId}-${index}`,
        label: runsMap?.get(runId) ?? 'Processing Run',
        color: CHIP_COLOURS.processing,
        onRemove: () => onRemoveFilter('runIds', runId),
      });
    });
  }

  // Boolean flags
  if (filters.hasKeyFacts) {
    pills.push({
      id: 'hasKeyFacts',
      label: 'Contains Key Facts',
      color: CHIP_COLOURS.key,
      onRemove: () => onRemoveFilter('hasKeyFacts'),
    });
  }

  if (filters.hasRelevantFacts) {
    pills.push({
      id: 'hasRelevantFacts',
      label: 'Contains Relevant Facts',
      color: CHIP_COLOURS.hasRelevantFacts,
      onRemove: () => onRemoveFilter('hasRelevantFacts'),
    });
  }

  if (filters.isPleading !== undefined) {
    pills.push({
      id: 'isPleading',
      label: filters.isPleading === true ? 'Pleadings' : 'Non-Pleadings',
      color: CHIP_COLOURS.isPleading,
      onRemove: () => onRemoveFilter('isPleading'),
    });
  }

  // Relativity metadata filters
  if (filters.metadataFilters?.length) {
    filters.metadataFilters.forEach((filter, index) => {
      const valueDisplay = Array.isArray(filter.value)
        ? filter.value.length > 2
          ? `${filter.value.length} selected`
          : filter.value.join(', ')
        : String(filter.value);

      // Use display name from lookup if available, otherwise use field name
      const fieldDisplayName = relativityFieldDisplayNames?.[filter.field] ?? filter.field;

      pills.push({
        id: `metadataFilter-${index}`,
        label: `${fieldDisplayName}: ${valueDisplay}`,
        color: CHIP_COLOURS.relativity,
        icon: 'lucide--database',
        onRemove: () => onRemoveFilter('metadataFilters', index),
      });
    });
  }

  // Document IDs filter
  if (filters.docIds?.length) {
    const docCount = filters.docIds.length;
    pills.push({
      id: 'docIds',
      label: `${docCount} ${docCount === 1 ? 'doc' : 'docs'} by ID`,
      color: CHIP_COLOURS.filters.doc,
      onRemove: () => onRemoveFilter('docIds'),
    });
  }

  return pills;
}

export function buildFactFilterPills(
  filters: FactFilters,
  onRemoveFilter: (key: string, value?: any) => void,
  personsMap?: Map<string, string>,
  issuesMap?: Map<string, string>,
): FilterPill[] {
  const pills: FilterPill[] = [];

  // Keyword search query
  if (filters.searchQuery && filters.searchQuery.trim()) {
    pills.push({
      id: 'searchQuery',
      label: filters.searchQuery,
      color: CHIP_COLOURS.default,
      onRemove: () => onRemoveFilter('searchQuery'),
    });
  }

  // Key facts filter
  if (filters.keyFacts) {
    pills.push({
      id: 'keyFacts',
      label: 'Key Facts',
      color: CHIP_COLOURS.key,
      onRemove: () => onRemoveFilter('keyFacts'),
    });
  }

  // Disputed facts filter
  if (filters.disputedFacts) {
    pills.push({
      id: 'disputedFacts',
      label: 'Disputed Facts',
      color: CHIP_COLOURS.disputed,
      onRemove: () => onRemoveFilter('disputedFacts'),
    });
  }

  // Included documents
  if (filters.docIds?.length) {
    const docCount = filters.docIds.filter((doc) => doc).length;
    pills.push({
      id: 'docIds',
      label: `${docCount} ${docCount === 1 ? 'doc' : 'docs'}`,
      color: CHIP_COLOURS.filters.doc,
      onRemove: () => onRemoveFilter('docIds'),
    });
  }

  // Excluded documents
  if (filters.excludedDocIds?.length) {
    const excludedCount = filters.excludedDocIds.filter((doc) => doc).length;
    pills.push({
      id: 'excludedDocIds',
      label: `${excludedCount} excluded ${excludedCount === 1 ? 'doc' : 'docs'}`,
      color: CHIP_COLOURS.default,
      onRemove: () => onRemoveFilter('excludedDocIds'),
    });
  }

  // Relevance levels
  if (filters.relevance?.length) {
    filters.relevance.forEach((rel, index) => {
      pills.push({
        id: `relevance-${rel}-${index}`,
        label: getRelevanceLabel(rel),
        color: CHIP_COLOURS.relevance[rel as keyof typeof CHIP_COLOURS.relevance] || CHIP_COLOURS.default,
        onRemove: () => onRemoveFilter('relevance', rel),
      });
    });
  }

  // Classifications
  if (filters.classification?.length) {
    filters.classification.forEach((cls, index) => {
      pills.push({
        id: `classification-${cls}-${index}`,
        label: getClassificationLabel(cls),
        color: CHIP_COLOURS.default,
        onRemove: () => onRemoveFilter('classification', cls),
      });
    });
  }

  addDocumentTypesPills(pills, filters.documentTypes, onRemoveFilter);
  addIssuesPills(pills, filters.issueIds, onRemoveFilter, issuesMap);
  addDateRangePills(pills, filters.startDate, filters.endDate, onRemoveFilter);

  // Persons/entities
  if (filters.personIds?.length) {
    filters.personIds.forEach((personId, index) => {
      pills.push({
        id: `person-${personId}-${index}`,
        label: getPersonLabel(personId, personsMap),
        color: CHIP_COLOURS.filters.person,
        onRemove: () => onRemoveFilter('personIds', personId),
      });
    });
  }

  return pills;
}

export function buildFilterPills(
  filters: DocFilters | FactFilters,
  entityType: 'document' | 'fact',
  onRemoveFilter: (key: string, value?: any) => void,
  personsMap?: Map<string, string>,
  issuesMap?: Map<string, string>,
): FilterPill[] {
  if (entityType === 'document') {
    return buildDocumentFilterPills(filters as DocFilters, onRemoveFilter);
  } else {
    return buildFactFilterPills(filters as FactFilters, onRemoveFilter, personsMap, issuesMap);
  }
}

export function buildInconsistencyFilterPills(
  filters: InconsistencyFilters,
  onRemoveFilter: (key: string, value?: any) => void,
): FilterPill[] {
  const pills: FilterPill[] = [];

  if (filters.hasRelevantFacts) {
    pills.push({
      id: 'hasRelevantFacts',
      label: 'Contains Relevant Facts',
      color: CHIP_COLOURS.hasRelevantFacts,
      onRemove: () => onRemoveFilter('hasRelevantFacts'),
    });
  }

  if (filters.types?.length) {
    filters.types.forEach((type, index) => {
      pills.push({
        id: `type-${type}-${index}`,
        label: getInconsistencyTypeDisplayName(type as InconsistencyType),
        color: CHIP_COLOURS.inconsistencyType,
        onRemove: () => onRemoveFilter('types', type),
      });
    });
  }

  if (filters.levels?.length) {
    filters.levels.forEach((level, index) => {
      pills.push({
        id: `level-${level}-${index}`,
        label: capitalizeFirstLetter(level),
        color: CHIP_COLOURS.levels[level as keyof typeof CHIP_COLOURS.levels] || CHIP_COLOURS.default,
        onRemove: () => onRemoveFilter('levels', level),
      });
    });
  }

  if (filters.sources?.length) {
    filters.sources.forEach((source, index) => {
      pills.push({
        id: `source-${source}-${index}`,
        label: getInconsistencySourceDisplayName(source as InconsistencySource),
        color: CHIP_COLOURS.inconsistencySource,
        onRemove: () => onRemoveFilter('sources', source),
      });
    });
  }

  if (filters.includedDocIds?.length) {
    const count = filters.includedDocIds.length;
    pills.push({
      id: 'includedDocIds',
      label: `${count} ${count === 1 ? 'doc' : 'docs'}`,
      color: CHIP_COLOURS.filters.doc,
      onRemove: () => onRemoveFilter('includedDocIds'),
    });
  }

  if (filters.excludedDocIds?.length) {
    const count = filters.excludedDocIds.length;
    pills.push({
      id: 'excludedDocIds',
      label: `${count} excluded ${count === 1 ? 'doc' : 'docs'}`,
      color: CHIP_COLOURS.default,
      onRemove: () => onRemoveFilter('excludedDocIds'),
    });
  }

  if (filters.isPleading !== undefined) {
    pills.push({
      id: 'isPleading',
      label: filters.isPleading === true ? 'Pleadings' : 'Non-Pleadings',
      color: CHIP_COLOURS.isPleading,
      onRemove: () => onRemoveFilter('isPleading'),
    });
  }

  return pills;
}

export function buildClientSideFilterPills(
  columnFilters: ColumnFiltersState,
  setColumnFilters: (filters: ColumnFiltersState | ((prev: ColumnFiltersState) => ColumnFiltersState)) => void,
): FilterPill[] {
  const pills: FilterPill[] = [];

  columnFilters.forEach((filter) => {
    const columnId = filter.id;
    const filterValue = filter.value;

    // Handle date range filters
    if (columnId === 'date') {
      const parseResult = DateRangeFilterSchema.safeParse(filterValue);
      if (!parseResult.success) return;

      const { startDate, endDate } = parseResult.data;

      const createDateRemoveHandler = (field: 'startDate' | 'endDate') => () => {
        setColumnFilters((prev) =>
          prev
            .map((f) =>
              f.id === 'date' ? { ...f, value: { ...(f.value as DateRangeFilterValue), [field]: null } } : f,
            )
            .filter((f) => {
              if (f.id !== 'date') return true;
              const dateVal = f.value as DateRangeFilterValue;
              return dateVal.startDate || dateVal.endDate;
            }),
        );
      };

      if (startDate) {
        pills.push({
          id: 'startDate',
          label: getDateLabel(startDate.toISOString(), 'From'),
          color: CHIP_COLOURS.filters.date,
          onRemove: createDateRemoveHandler('startDate'),
        });
      }

      if (endDate) {
        pills.push({
          id: 'endDate',
          label: getDateLabel(endDate.toISOString(), 'To'),
          color: CHIP_COLOURS.filters.date,
          onRemove: createDateRemoveHandler('endDate'),
        });
      }

      return;
    }

    // Handle array-based filter values (from multi-select)
    if (Array.isArray(filterValue) && filterValue.length > 0) {
      filterValue.forEach((value, index) => {
        let label = value;
        let color = CHIP_COLOURS.extension;

        // Customize label and color based on column type
        if (columnId === 'type') {
          label = getDocumentTypeLabel(value);
          color = CHIP_COLOURS.type;
        } else if (columnId === 'file.extension') {
          label = getExtensionLabel(value);
          color = CHIP_COLOURS.extension;
        } else if (columnId === 'issues') {
          label = value;
          color = CHIP_COLOURS.issue;
        } else if (columnId === 'file.fileMetadata.relativityFields.issues') {
          label = `Rel. Issue: ${value}`;
          color = CHIP_COLOURS.relativity;
        } else if (columnId === 'file.createdDate') {
          label = `Run at ${value}`;
          color = CHIP_COLOURS.filters.date;
        } else if (columnId === 'persons') {
          label = value;
          color = CHIP_COLOURS.filters.person;
        } else if (columnId === 'level') {
          label = capitalizeFirstLetter(value);
          color = CHIP_COLOURS.levels[value as keyof typeof CHIP_COLOURS.levels] || CHIP_COLOURS.extension;
        } else if (columnId === 'source') {
          label = getInconsistencySourceDisplayName(value);
          color = CHIP_COLOURS.inconsistencySource;
        }

        pills.push({
          id: `${columnId}-${value}-${index}`,
          label,
          color,
          onRemove: () => {
            // Remove this specific value from the filter
            const updatedFilters = columnFilters
              .map((f) => {
                if (f.id === columnId) {
                  const newValues = (f.value as string[]).filter((v) => v !== value);
                  return newValues.length > 0 ? { ...f, value: newValues } : null;
                }
                return f;
              })
              .filter((f): f is { id: string; value: any } => f !== null);

            setColumnFilters(updatedFilters);
          },
        });
      });
    }
  });

  return pills;
}
